/**
 * Shared axis behaviour for growth-of-$100 charts.
 *
 * Two screens draw the same picture — the portfolio against its
 * benchmarks, a vault against its own — and a growth index only reads
 * correctly under two rules that recharts will not apply for you.
 *
 * Anchoring the axis at zero is the one that ruins the chart: a series
 * that hovers near 100 gets pinned flat against the top of a 0–120 axis
 * with four fifths of the canvas empty, so a real 3% move is invisible and
 * a flat vault looks identical to a collapsed one. Padding the observed
 * range instead puts the moves where the eye is.
 *
 * Kept in `lib` rather than either feature because features must not reach
 * into each other, and two consumers is where a helper stops belonging to
 * whichever screen happened to need it first.
 */

import { formatDay } from './format/date-time/day';

/**
 * A padded domain around the values actually present.
 *
 * The floor is clamped at zero: these are growth indexes anchored at 100
 * and none can go negative, so padding must never invent a subzero floor
 * and hand a third of the canvas to impossible values.
 */
export function growthYDomain(
  points: readonly object[],
  padFraction = 0.15
): [number, number] | undefined {
  let min = Infinity;
  let max = -Infinity;
  for (const point of points) {
    for (const [key, value] of Object.entries(point)) {
      if (key === 'date' || typeof value !== 'number') continue;
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
  // A dead-flat series has no range to pad, so give it a visible band
  // rather than a zero-height domain recharts would collapse.
  const pad = Math.max(1, (max - min) * padFraction);
  return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
}

/** `2026-05-18` is storage format. An axis reads `May 18`. */
export function formatGrowthTick(timestamp: string): string {
  return formatDay(timestamp);
}

/**
 * The growth-chart palette, shared so the vault page and the portfolio
 * page render the same idea in the same colors.
 *
 * The subject series takes chart-2 and every benchmark overlay is assigned
 * from the ramp by selection order, so a reader who has learned that green
 * is "mine" on one screen reads it the same way on the other.
 */
export const GROWTH_SUBJECT_COLOR = 'var(--color-rowa-chart-2)';

const GROWTH_BENCHMARK_COLORS = [
  'var(--color-rowa-chart-5)',
  'var(--color-rowa-chart-6)',
  'var(--color-rowa-chart-7)',
  'var(--color-rowa-chart-1)',
  'var(--color-rowa-chart-3)'
] as const;

export function benchmarkColor(position: number): string {
  return GROWTH_BENCHMARK_COLORS[
    position % GROWTH_BENCHMARK_COLORS.length
  ] as string;
}
